/** @jsx jsx */
import { Flex, FormLabel, Paragraph } from "@eworkit/iris";
import { FC, useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { DEFAULT_PAYMENT_TERM_REDUCTION } from "@/data/payexpress/service";
import { PayexpressTermsOfService } from "@/data/payexpress/types/PayexpressTermsOfService";
import { useOnLanguageChange } from "@/hooks/Translations";
import { JobApplicationPayExpressRadioBox } from "@/views/job-applications/apply/_details/_compontents/JobApplicationPayExpressRadioBox";
import { JobApplicationPayExpressTermsOfServiceModal } from "@/views/job-applications/apply/_details/_compontents/JobApplicationPayExpressTermsOfServiceModal";

interface ApplyPayExpressProps {
  jobRequest: JobRequest;
  feeAmount?: string;
  payExpressChosen?: boolean;
  onChange: (isPayExpress: boolean, fee: number, term: number) => void;
  onPayExpressUnavailable: () => void;
  requiredError?: boolean;
}

export const ApplyPayexpress: FC<ApplyPayExpressProps> = ({
  jobRequest,
  feeAmount,
  onChange,
  payExpressChosen,
  onPayExpressUnavailable,
  requiredError,
}) => {
  const { t } = useTranslation("session");

  const currentLang = useOnLanguageChange();

  const termsAndConditionLink =
    currentLang === "sv"
      ? "https://knowledge.eworkgroup.com/sv/villkor-for-payexpress"
      : "https://knowledge.eworkgroup.com/terms-and-conditions-payexpress";

  const payExpressLink =
    currentLang === "sv"
      ? "https://www.eworkgroup.com/sv/tjanster/konsulter-partners/hur-det-fungerar/payexpress"
      : "https://www.eworkgroup.com/services/professionals/how-it-works/payexpress";

  const modalState = useDialogState({ animated: true });

  const [isPayExpressChosen, setIsPayExpressChosen] = useState<boolean>(payExpressChosen ?? false);
  const [isInitialCalculationsExecuted, setIsInitialCalculationsExecuted] = useState<boolean>(false);

  const eworkTerm = 5;

  const supplierSpecifiedPaymentTerms = Boolean(
    jobRequest.supplierPaymentTerm && jobRequest.rate.clientRateType !== "FIXED",
  );

  const payExpressFeePercentage = jobRequest.payExpressConfig?.payExpressFeePercentage ?? 0;
  const isPayExpressDefaultOption = jobRequest.payExpressConfig?.defaultPayExpress ?? false;
  const supplierPaymentTermDays = jobRequest.supplierPaymentTerm ?? 0;

  // TODO
  const TERMS_OF_SERVICE: PayexpressTermsOfService = {
    content: "terms_of_service",
    language: "en",
    isDefault: true,
  };

  const handleOptionCheck = useCallback(
    (isPayExpress, term) => {
      setIsPayExpressChosen(isPayExpress);
      onChange(isPayExpress, payExpressFeePercentage, term);
    },
    [onChange, payExpressFeePercentage],
  );

  useEffect(() => {
    if (!isInitialCalculationsExecuted) {
      onPayExpressUnavailable();
      setIsInitialCalculationsExecuted(true);
    }
    if (jobRequest?.payExpressConfig?.payExpressEnabled && !isInitialCalculationsExecuted) {
      setIsPayExpressChosen(!!payExpressChosen);
      onChange(
        !!payExpressChosen,
        payExpressFeePercentage,
        payExpressChosen ? DEFAULT_PAYMENT_TERM_REDUCTION : supplierPaymentTermDays,
      );
      setIsInitialCalculationsExecuted(true);
    }
  }, [
    supplierPaymentTermDays,
    isInitialCalculationsExecuted,
    isPayExpressDefaultOption,
    jobRequest.payExpressConfig,
    onChange,
    onPayExpressUnavailable,
    payExpressChosen,
    payExpressFeePercentage,
  ]);

  if (!jobRequest?.payExpressConfig?.payExpressEnabled) {
    return null;
  }

  return (
    <Flex gap="xxs" flexDirection="column">
      <FormLabel label={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.LABEL")} required />

      <Flex gap="xs" flexDirection="column">
        <JobApplicationPayExpressRadioBox
          checked={isPayExpressChosen}
          recommended={isPayExpressDefaultOption}
          title={
            supplierSpecifiedPaymentTerms
              ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYMENT_IN", {
                  days: DEFAULT_PAYMENT_TERM_REDUCTION,
                })
              : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.INTERESTED_IN_PAYEXPRESS")
          }
          feeAmount={feeAmount}
          showFeeAmount={supplierSpecifiedPaymentTerms}
          term={DEFAULT_PAYMENT_TERM_REDUCTION}
          onCheckAction={term => handleOptionCheck(true, term)}
          error={requiredError}>
          <Paragraph>
            <Trans
              ns="session"
              i18nKey={
                supplierSpecifiedPaymentTerms
                  ? "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.DECREASE_INFO_NET"
                  : "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.NO_TERM_PAYEXPRESS_OPTION_TITLE"
              }
              values={{
                paymentTermDays: jobRequest.supplierPaymentTerm,
                feeRate: payExpressFeePercentage,
                paymentTermReduction: DEFAULT_PAYMENT_TERM_REDUCTION,
                days: DEFAULT_PAYMENT_TERM_REDUCTION,
              }}>
              <a href={payExpressLink} target="_blank" rel="noopener noreferrer"></a>
            </Trans>
          </Paragraph>

          {supplierSpecifiedPaymentTerms && (
            <Paragraph>
              <Trans ns="session" i18nKey="JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.DECREASE_INFO_TERMS">
                <a href={termsAndConditionLink} target="_blank" rel="noopener noreferrer"></a>
              </Trans>
            </Paragraph>
          )}
        </JobApplicationPayExpressRadioBox>

        <JobApplicationPayExpressRadioBox
          checked={!isPayExpressChosen}
          recommended={false}
          title={
            supplierSpecifiedPaymentTerms
              ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.PAYMENT_IN", {
                  days: supplierPaymentTermDays + eworkTerm,
                })
              : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.NO_TERM_FREE_OPTION_TITLE")
          }
          feeAmount={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.FREE")}
          term={supplierPaymentTermDays}
          onCheckAction={term => handleOptionCheck(false, term)}
          error={requiredError}>
          <Paragraph>
            {supplierSpecifiedPaymentTerms
              ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.STANDARD_INFO", {
                  paymentTermDays: supplierPaymentTermDays,
                  eworkTerm: eworkTerm,
                })
              : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.PAYEXPRESS.NO_TERM_FREE_OPTION_TEXT", {
                  eworkTerm: eworkTerm,
                })}
          </Paragraph>
        </JobApplicationPayExpressRadioBox>
      </Flex>

      <JobApplicationPayExpressTermsOfServiceModal dialogState={modalState} data={TERMS_OF_SERVICE} />
    </Flex>
  );
};
